import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { BrandLogo } from '@calendly/ui/components/brand-logo'
import { SimpleSelect } from '@calendly/ui/components/select'
import { SwitchInput } from '@calendly/ui/components/switch-input'
import { BodyText } from '@calendly/ui/components/typography'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useEventType } from 'features/event_type_provider'
import { useTrack } from 'hooks/analytics_events'
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { useFeatureFlagEncountered } from 'hooks/analytics_events/useFeatureFlagEncountered'

import {
  useCreateBookingCodeMutation,
  useDeleteBookingCodeMutation,
  useGetBookingCodesQuery,
  useUpdateBookingCodeMutation
} from './api'
import {
  DropdownButtonWrapper,
  DropdownWrapper,
  FormSection,
  OptionContent,
  OptionLogo,
  SectionWrapper,
  SwitchWrapper
} from './commerce_section_styles'
import { PaymentAmount } from './payment_amount'
import { PaymentCoupons } from './payment_coupons'
import {
  computePaymentsEnabledState,
  getPaypalFormRowsVisibility,
} from './payment_provider_utils'
import { PaymentTerms } from './payment_terms'
import { StatusBanner } from './status_banner'

const buildProviderOptions = ({
  isStripeEnabled,
  isPaypalLegacyEnabled,
  isPaypalPpcpEnabled,
  paypalRows,
}) => {
  const options = [{
    name: 'Stripe',
    value: 'stripe',
    disabled: !isStripeEnabled,
  }]
  if (paypalRows.showLegacyRow || paypalRows.showDisconnectedPaypalFallback) {
    options.push({
      name: 'PayPal',
      value: 'paypal',
      disabled: !isPaypalLegacyEnabled,
    })
  }
  if (paypalRows.showPpcpRow) {
    options.push({
      name: 'PayPal',
      value: 'paypal_ppcp',
      disabled: !isPaypalPpcpEnabled,
    })
  }
  return options
}

const PROVIDERS_LOGOS = {
  stripe: 'stripe',
  paypal: 'pay-pal',
  paypal_ppcp: 'pay-pal',
}

export const CommerceSection = ({
  provider,
  paymentMethodEnabled,
  isUpgradeRequired,
  isStripeConnected,
  isPaypalConnected,
  isPaypalPpcpConnected,
  isPaypalPpcpFeatureEnabled,
}) => {
  const { setFieldValue } = useFormContext()
  const track = useTrack()
  const [showForm, setShowForm] = useState(provider !== 'none')
  const [isProviderDisabled, setIsProviderDisabled] = useState(false)
  const [stripeEnabled, setStripeEnabled] = useState(false)
  const [paypalLegacyEnabled, setPaypalLegacyEnabled] = useState(false)
  const [paypalPpcpEnabled, setPaypalPpcpEnabled] = useState(false)
  const paymentCouponsRef = useRef(null)
  useFeatureFlagEncountered('commerce_booking_codes_feature')

  const eventType = useEventType()
  const { isMETParent } = eventType

  // Fetch coupons data
  const { data: couponsData, isLoading: isLoadingCoupons, error: couponsError } = useGetBookingCodesQuery({
    eventTypeUuid: eventType?.uuid,
    count: 100
  }, {
    skip: !eventType?.uuid
  })

  const filteredCoupons = useMemo(() => {
    if (!couponsData?.collection) return []
    return couponsData.collection
  }, [couponsData?.collection])

  // Coupon mutation hooks
  const [createBookingCode, { isLoading: isCreating }] = useCreateBookingCodeMutation()
  const [deleteBookingCode, { isLoading: isDeleting }] = useDeleteBookingCodeMutation()
  const [updateBookingCode, { isLoading: isUpdating }] = useUpdateBookingCodeMutation()

  const isStripeDisconnected = paymentMethodEnabled && provider === 'stripe' && !isStripeConnected
  const isPaypalLegacyDisconnected = paymentMethodEnabled && provider === 'paypal' && !isPaypalConnected
  const isPaypalPpcpDisconnected = paymentMethodEnabled && provider === 'paypal_ppcp' && !isPaypalPpcpConnected
  const isPaypalDisconnected = isPaypalLegacyDisconnected || isPaypalPpcpDisconnected

  const paypalRows = useMemo(() => getPaypalFormRowsVisibility({
    isPaypalConnected,
    isPaypalPpcpConnected,
    provider,
  }), [isPaypalConnected, isPaypalPpcpConnected, provider])

  useEffect(() => {
    const ui = computePaymentsEnabledState({
      isPaymentsIntegrationAvailable: !isUpgradeRequired,
      isPaypalConnected,
      isPaypalPpcpConnected,
      isPaypalPpcpFeatureEnabled,
      isPaypalDisconnected,
      isStripeConnected,
      isStripeDisconnected,
      paymentMethodEnabled,
      provider
    })

    setIsProviderDisabled(ui.isProviderDisabled)
    setStripeEnabled(ui.isStripeEnabled)
    setPaypalLegacyEnabled(ui.isPaypalLegacyEnabled)
    setPaypalPpcpEnabled(ui.isPaypalPpcpEnabled)
  }, [
    isUpgradeRequired,
    isPaypalConnected,
    isPaypalPpcpConnected,
    isPaypalPpcpFeatureEnabled,
    isPaypalDisconnected,
    isStripeConnected,
    isStripeDisconnected,
    paymentMethodEnabled,
    provider
  ])

  // Update showForm when provider changes
  useEffect(() => {
    setShowForm(provider !== 'none')
  }, [provider])

  const providerOptions = buildProviderOptions({
    isStripeEnabled: stripeEnabled,
    isPaypalLegacyEnabled: paypalLegacyEnabled,
    isPaypalPpcpEnabled: paypalPpcpEnabled,
    paypalRows,
  })

  const handleProviderSelection = (selectedProvider) => {
    const selectedOption = providerOptions.find((option) => option.value === selectedProvider)

    if (selectedOption && selectedOption.disabled) {
      window.open('/integrations?category=payments', '_blank')
    } else {
      setFieldValue('provider', selectedProvider)
    }
  }

  const handleSwitchToggle = () => {
    const newShowForm = !showForm
    setShowForm(newShowForm)

    if (!newShowForm) {
      // When switch is turned off, set provider to 'none'
      setFieldValue('provider', 'none')
      track({
        name: TRACKING_EVENT_NAMES.EVENT_TYPE_EDITOR_INTERACTION,
        properties: {
          event_type_uuid: eventType.uuid,
          section: 'payments',
          action: 'disable_payment',
        }
      })
    } else {
      track({
        name: TRACKING_EVENT_NAMES.EVENT_TYPE_EDITOR_INTERACTION,
        properties: {
          event_type_uuid: eventType.uuid,
          section: 'payments',
          action: 'enable_payment',
        }
      })
      let defaultProvider = 'none'
      if (stripeEnabled) {
        defaultProvider = 'stripe'
      } else if (paypalPpcpEnabled && isPaypalPpcpConnected) {
        defaultProvider = 'paypal_ppcp'
      } else if (paypalLegacyEnabled && isPaypalConnected) {
        defaultProvider = 'paypal'
      }
      setFieldValue('provider', defaultProvider)
    }
  }

  const handleCurrencyChange = (newCurrency) => {
    if (paymentCouponsRef.current) {
      paymentCouponsRef.current.closeModal()
    }
  }

  return (
    <SectionWrapper>
      <StatusBanner
        provider={provider}
        isStripeConnected={isStripeConnected}
        isPaypalConnected={isPaypalConnected}
        isPaypalPpcpConnected={isPaypalPpcpConnected}
        isPaypalPpcpFeatureEnabled={isPaypalPpcpFeatureEnabled}
      />
      <SwitchWrapper>
        <BodyText size="medium">{I18n.t('publisher.event_types.payment_method.require_payment_switch')}</BodyText>
        <label>
          <SwitchInput
            name="paymentSwitch"
            checked={showForm}
            onChange={handleSwitchToggle}
            disabled={isProviderDisabled}
          />
        </label>
      </SwitchWrapper>
      {showForm && (
      <>
        <FormSection>
          <DropdownButtonWrapper>
            <BodyText weight="bold">
              {I18n.t('publisher.event_types.payment_method.payment_processor_label')}
              :
            </BodyText>
            <DropdownWrapper>
              <SimpleSelect
                aria-label={I18n.t('publisher.event_types.payment_method.payment_processor_label')}
                variant="minimal"
                value={provider}
                onChange={handleProviderSelection}
                options={providerOptions.map((option) => ({
                  value: option.value,
                  name: (
                    <OptionContent>
                      <OptionLogo>
                        <BrandLogo name={PROVIDERS_LOGOS[option.value]} />
                      </OptionLogo>
                      {option.name}
                    </OptionContent>
                  ),
                  disabled: option.disabled,
                }))}
              />
            </DropdownWrapper>
          </DropdownButtonWrapper>
        </FormSection>
        <FormSection>
          <PaymentAmount
            label={I18n.t('publisher.event_types.payment_method.amount_to_collect_label')}
            bold
            newForm
            coupons={filteredCoupons}
            onDeleteCoupon={deleteBookingCode}
            onCurrencyChange={handleCurrencyChange}
          />
        </FormSection>
        {provider === 'stripe' && !isMETParent && (
          <FormSection>
            <PaymentCoupons
              ref={paymentCouponsRef}
              coupons={filteredCoupons}
              isLoading={isLoadingCoupons}
              error={couponsError}
              onCreateCoupon={createBookingCode}
              onUpdateCoupon={updateBookingCode}
              onDeleteCoupon={deleteBookingCode}
              eventType={eventType}
              isCreating={isCreating}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
            />
          </FormSection>
        )}
        <FormSection>
          <PaymentTerms bold newForm />
        </FormSection>
      </>
      )}
    </SectionWrapper>
  )
}
